import { useLocalStorage } from '@vueuse/core';
import { defineStore, type StoreDefinition } from 'pinia';
import { computed, type ComputedRef, type Ref } from 'vue';
import type { Notification } from '@/domain/model/business/Notification.ts';

interface NotificationFromApi {
  id: number;
  title: string;
  message: string;
}

export const useNotificationsStore: StoreDefinition = defineStore(
  'notificationsStore',
  () => {
    const lastFetched: Ref<Date> = useLocalStorage(
      'notifications_last_fetched',
      new Date(),
    );

    const notifications: Ref<Notification[]> = useLocalStorage(
      'notifications',
      [],
    );

    function updateLastFetched(): void {
      lastFetched.value = new Date();
    }

    function mergeWithCurrentState(
      newNotifications: NotificationFromApi[],
    ): void {
      notifications.value = newNotifications.map(
        (newNotification: NotificationFromApi): Notification => {
          const existing: Notification | undefined = findNotification(
            newNotification.id,
          );

          return {
            id: newNotification.id,
            title: newNotification.title,
            message: newNotification.message,
            seen: existing?.seen ?? false,
          };
        },
      );
    }

    const lastNotificationNotYetShown: ComputedRef<Notification | undefined> =
      computed(() => {
        const notificationsNotYetSeen: Notification[] =
          notifications.value.filter((n: Notification) => !n.seen);

        return notificationsNotYetSeen[0];
      });

    function markAsShown(id: number): void {
      const notificationToMark: Notification | undefined = findNotification(id);

      if (!notificationToMark) {
        return;
      }

      notificationToMark.seen = true;
    }

    function findNotification(id: number): Notification | undefined {
      return notifications.value.find(
        (n: Notification): boolean => n.id === id,
      );
    }

    return {
      lastFetched,
      notifications,
      lastNotificationNotYetShown,
      updateLastFetched,
      markAsShown,
      mergeWithCurrentState,
    };
  },
);
